import React, { useState, useEffect, useRef } from 'react';
import {
	PlusIcon,
	PencilSquareIcon,
	ShieldCheckIcon,
	InformationCircleIcon,
} from '@heroicons/react/24/outline';

import { ESignatureList, StackableModal } from '../../../components/nextgen';
import SignaturePad from '../../../components/SignaturePad';

import {
	getEsignatures,
	createEsignature,
	updateEsignature,
	deleteEsignature,
} from '../../../api';

// E-Signature Terms and Conditions
const ESIGNATURE_TERMS = `By creating and using this electronic signature, I agree that:

1. My electronic signature is the legal equivalent of my handwritten signature.
2. I consent to use electronic signatures for signing documents in this system.
3. I am the authorized user of this account and am responsible for all signatures created.
4. I understand that my signature will be recorded with a timestamp and linked to my account.
5. This signature may be used for compliance and audit purposes.`;

const ESignaturesSettings = () => {
	const [signatures, setSignatures] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [newSignatureData, setNewSignatureData] = useState(null);
	const [newSignatureName, setNewSignatureName] = useState('');
	const [termsAccepted, setTermsAccepted] = useState(false);
	const [showTerms, setShowTerms] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	const [error, setError] = useState(null);

	// Fetch signatures
	const fetchSignatures = async () => {
		setLoading(true);
		try {
			const data = await getEsignatures();
			setSignatures(data || []);
		} catch (err) {
			console.error('Failed to fetch signatures:', err);
			setError('Failed to load signatures');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchSignatures();
	}, []);

	// Create new signature
	const handleCreate = async () => {
		if (!newSignatureData) {
			setError('Please draw your signature');
			return;
		}
		if (!newSignatureName.trim()) {
			setError('Please enter a name for your signature');
			return;
		}
		if (!termsAccepted) {
			setError('Please accept the terms and conditions');
			return;
		}

		setIsCreating(true);
		setError(null);

		try {
			await createEsignature({
				name: newSignatureName.trim(),
				signatureData: newSignatureData,
				termsAccepted: true,
				setAsDefault: signatures.length === 0,
			});

			setShowCreateModal(false);
			setNewSignatureData(null);
			setNewSignatureName('');
			setTermsAccepted(false);
			fetchSignatures();
		} catch (err) {
			console.error('Failed to create signature:', err);
			setError('Failed to create signature. Please try again.');
		} finally {
			setIsCreating(false);
		}
	};

	// Set signature as default
	const handleSetDefault = async (id) => {
		try {
			await updateEsignature({ id, setAsDefault: true });
			fetchSignatures();
		} catch (err) {
			console.error('Failed to set default:', err);
		}
	};

	// Rename signature
	const handleRename = async (id, name) => {
		try {
			await updateEsignature({ id, name });
			fetchSignatures();
		} catch (err) {
			console.error('Failed to rename:', err);
		}
	};

	// Delete signature
	const handleDelete = async (id) => {
		try {
			await deleteEsignature({ id });
			fetchSignatures();
		} catch (err) {
			console.error('Failed to delete:', err);
		}
	};

	const closeCreateModal = () => {
		setShowCreateModal(false);
		setNewSignatureData(null);
		setNewSignatureName('');
		setTermsAccepted(false);
		setShowTerms(false);
		setError(null);
	};

	return (
		<div>
			{/* Header */}
			<div className="flex items-center justify-between mb-6">
				<div>
					<h2 className="text-base font-semibold leading-6 text-gray-900">
						E-Signatures
					</h2>
					<p className="mt-1 text-sm text-gray-500">
						Manage your electronic signatures for signing documents and SOPs.
					</p>
				</div>
				<button
					type="button"
					onClick={() => setShowCreateModal(true)}
					className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
				>
					<PlusIcon className="h-4 w-4" />
					New Signature
				</button>
			</div>

			{/* Info Banner */}
			<div className="mb-6 rounded-lg bg-blue-50 p-4">
				<div className="flex">
					<div className="flex-shrink-0">
						<ShieldCheckIcon className="h-5 w-5 text-blue-400" />
					</div>
					<div className="ml-3">
						<h3 className="text-sm font-medium text-blue-800">
							Secure E-Signatures
						</h3>
						<div className="mt-2 text-sm text-blue-700">
							<p>
								Your signatures are securely stored and can be used to sign
								documents, approve SOPs, and other compliance-related tasks.
								Each signature includes a timestamp and is linked to your
								account for audit purposes.
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Signatures List */}
			{loading ? (
				<div className="flex items-center justify-center py-12">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
					<span className="ml-3 text-gray-500">Loading signatures...</span>
				</div>
			) : signatures.length === 0 ? (
				<div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
					<PencilSquareIcon className="mx-auto h-12 w-12 text-gray-400" />
					<h3 className="mt-2 text-sm font-semibold text-gray-900">
						No signatures
					</h3>
					<p className="mt-1 text-sm text-gray-500">
						Get started by creating your first e-signature.
					</p>
					<div className="mt-6">
						<button
							type="button"
							onClick={() => setShowCreateModal(true)}
							className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
						>
							<PlusIcon className="h-4 w-4" />
							Create Signature
						</button>
					</div>
				</div>
			) : (
				<ESignatureList
					signatures={signatures}
					onSetDefault={handleSetDefault}
					onRename={handleRename}
					onDelete={handleDelete}
					columns={3}
				/>
			)}

			{/* Create Signature Modal */}
			<StackableModal
				open={showCreateModal}
				onClose={closeCreateModal}
				title="Create New Signature"
				size="lg"
			>
				<div className="space-y-4">
					{error && (
						<div className="rounded-md bg-red-50 p-4">
							<p className="text-sm text-red-700">{error}</p>
						</div>
					)}

					{/* Signature Name */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Signature Name
						</label>
						<input
							type="text"
							value={newSignatureName}
							onChange={(e) => setNewSignatureName(e.target.value)}
							placeholder="e.g., My Primary Signature"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
						/>
					</div>

					{/* Signature Pad */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Draw Your Signature
						</label>
						<SignaturePad
							onSignatureChange={setNewSignatureData}
							width={500}
							height={150}
						/>
					</div>

					{/* Terms and Conditions */}
					<div className="border-t border-gray-200 pt-4">
						<div className="flex items-start gap-3">
							<input
								type="checkbox"
								checked={termsAccepted}
								onChange={(e) => setTermsAccepted(e.target.checked)}
								className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
							/>
							<div className="flex-1">
								<label
									className="text-sm text-gray-700 cursor-pointer"
									onClick={() => setTermsAccepted(!termsAccepted)}
								>
									I agree to the{' '}
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											setShowTerms(!showTerms);
										}}
										className="text-indigo-600 hover:text-indigo-500 underline"
									>
										E-Signature Terms and Conditions
									</button>
								</label>
							</div>
						</div>

						{showTerms && (
							<div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200 text-xs text-gray-600 whitespace-pre-line max-h-40 overflow-y-auto">
								{ESIGNATURE_TERMS}
							</div>
						)}
					</div>

					{/* Actions */}
					<div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
						<button
							type="button"
							onClick={closeCreateModal}
							disabled={isCreating}
							className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={handleCreate}
							disabled={isCreating || !newSignatureData || !termsAccepted}
							className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isCreating ? (
								<>
									<span className="inline-block animate-spin mr-2">...</span>
									Creating...
								</>
							) : (
								'Create Signature'
							)}
						</button>
					</div>
				</div>
			</StackableModal>
		</div>
	);
};

export default ESignaturesSettings;
